const dbg=(...a)=>{if(typeof window!=="undefined"&&window.__XTRC_DEBUG)console["log"](...a)};
const { decodeAIFF, isAIFF } = require('./aiff-decoder.cjs');
// ----------------------------------------------------------------------------
// draw-utils.js — shared canvas drawing helpers
// Used by all adapters to draw waveforms and access theme colors.
// Reads from window.currentTheme (set by index.html / theme editor).
// c0 = darkest bg, cf = lightest fg.
// ----------------------------------------------------------------------------

// Global audio playback state
let globalAudioSource = null;
let globalAudioContext = null;
let allActiveSources = new Set(); // Track all active audio sources globally

// Global audio manager singleton
const audioManager = {
   activeSource: null,
   activeComponent: null, // Track which component is currently playing ('adapter', 'bucket', 'export')
   activeCanvas: null, // Track canvas for cleanup on stop
   
   play(audioBuffer, audioCtx, component, options = {}) {
      const { playbackRate = 1, canvas, onEnded } = options;
      
      dbg('[audioManager] play called, component:', component);
      
      // Stop any existing playback (without component to stop everything)
      this.stop();
      
      // Get or create AudioContext
      const ctx = audioCtx || globalAudioContext || new (window.AudioContext || window.webkitAudioContext)();
      if (!globalAudioContext) globalAudioContext = ctx;
      
      // Resume if suspended
      if (ctx.state === 'suspended') {
         ctx.resume().catch(() => {});
      }
      
      // Peak-normalize the audio buffer to prevent loudness differences
      // between different bit depths and source formats (prevents deafening)
      const TARGET_PEAK = 0.7; // -3dBFS target
      let peak = 0;
      for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
         const data = audioBuffer.getChannelData(ch);
         for (let i = 0; i < data.length; i++) {
            const abs = Math.abs(data[i]);
            if (abs > peak) peak = abs;
         }
      }
      const normGain = peak > 0.001 ? Math.min(1.0, TARGET_PEAK / peak) : 1.0;
      
      // Create and start source
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      
      // Insert gain node for normalization
      const gainNode = ctx.createGain();
      gainNode.gain.value = normGain;
      
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      source.playbackRate.value = playbackRate;
      source.start();
      
      // Track this source
      this.activeSource = source;
      this.activeComponent = component;
      this.activeCanvas = canvas || null;
      globalAudioSource = source;
      allActiveSources.add(source);
      
      dbg('[audioManager] play completed, activeComponent:', this.activeComponent);
      
      // Cleanup when ended
      source.onended = () => {
         dbg('[audioManager] source onended, component:', component);
         if (this.activeSource === source) {
            this.activeSource = null;
            this.activeComponent = null;
         }
         if (globalAudioSource === source) {
            globalAudioSource = null;
         }
         allActiveSources.delete(source);
         try {
            source.disconnect();
            gainNode.disconnect();
         } catch (e) {
            // Ignore if already disconnected
         }
         // Remove playing class from canvas if provided
         if (canvas) {
            canvas.classList.remove('playing');
            canvas.classList.remove('xep-canvas-playing');
            // Clear inline border style set by export modal and other components
            canvas.style.border = '';
         }
         if (this.activeCanvas === canvas) this.activeCanvas = null;
         // Call custom onEnded callback if provided
         if (onEnded) onEnded();
      };
      
      return source;
   },
   
   stop(component = null) {
      dbg('[audioManager] stop called, component:', component, 'activeComponent:', this.activeComponent);
      
      // If a component is specified, only stop if it's not the active one
      if (component && this.activeComponent === component) {
         dbg('[audioManager] skipping stop (same component)');
         return; // Don't stop ourselves
      }
      
      // Stop the active source
      if (this.activeSource) {
         try {
            this.activeSource.stop();
            this.activeSource.disconnect();
         } catch (e) {
            // Ignore if already stopped
         }
         this.activeSource = null;
         this.activeComponent = null;
      }
      
      // Clean up active canvas visual state
      if (this.activeCanvas) {
         this.activeCanvas.classList.remove('playing');
         this.activeCanvas.classList.remove('xep-canvas-playing');
         this.activeCanvas.style.border = '';
         this.activeCanvas = null;
      }
      
      // Also stop global source
      if (globalAudioSource) {
         try {
            globalAudioSource.stop();
            globalAudioSource.disconnect();
         } catch (e) {
            // Ignore if already stopped
         }
         globalAudioSource = null;
      }
      
      // Stop all active sources
      for (const source of allActiveSources) {
         try {
            source.stop();
            source.disconnect();
         } catch (e) {
            // Ignore if already stopped
         }
      }
      allActiveSources.clear();
      
      dbg('[audioManager] stop completed');
   }
};

/**
 * Get or create the global AudioContext (for playback — requires user gesture)
 * @returns {AudioContext}
 */
function getGlobalAudioContext() {
   if (!globalAudioContext) {
      globalAudioContext = new (window.AudioContext || window.webkitAudioContext)();
   }
   if (globalAudioContext.state === 'suspended') {
      globalAudioContext.resume().catch(() => {});
   }
   return globalAudioContext;
}

/**
 * Get an AudioContext suitable for decoding audio data without requiring
 * a user gesture. Uses OfflineAudioContext when available (no autoplay
 * policy restriction), falls back to the global AudioContext otherwise.
 * @returns {AudioContext|OfflineAudioContext}
 */
let _offlineCtx = null;
function getDecodeAudioContext() {
   if (_offlineCtx) return _offlineCtx;
   const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
   if (OAC) {
      // A minimal OfflineAudioContext is sufficient for decodeAudioData
      _offlineCtx = new OAC(1, 1, 44100);
      return _offlineCtx;
   }
   // Fallback: use the global AudioContext (may trigger autoplay warnings)
   return getGlobalAudioContext();
}

/**
 * Stop all audio playback globally
 * @param {string} [component] - Optional component name to exclude from stopping
 */
function stopAllAudioPlayback(component = null) {
   audioManager.stop(component);
}

/**
 * Play audio from an AudioBuffer (global singleton - stops any existing playback)
 * @param {AudioBuffer} audioBuffer - The audio buffer to play
 * @param {AudioContext} [audioCtx] - Optional AudioContext to use
 * @param {number} [playbackRate] - Optional playback rate (default: 1)
 * @param {HTMLElement} [canvas] - Optional canvas element to track playing state
 * @param {Function} [onEnded] - Optional callback when playback ends
 * @param {string} [component] - Component name ('adapter', 'bucket', 'export')
 * @returns {AudioBufferSourceNode} The created source node
 */
function playAudioBuffer(audioBuffer, audioCtx, playbackRate = 1, canvas, onEnded, component = 'adapter') {
   return audioManager.play(audioBuffer, audioCtx, component, { playbackRate, canvas, onEnded });
}

/**
 * Returns { bg, fg, mid } from the active theme.
 * Handles both array (window.themeColors) and object (window.currentTheme.data) forms.
 */
function getThemeDrawColors() {
   dbg('[draw-utils] getThemeDrawColors called, window.themeColors:', window.themeColors, 'window.currentTheme:', window.currentTheme);

   // First try window.themeColors (array form used by xtrc)
   if (window.themeColors && Array.isArray(window.themeColors) && window.themeColors.length > 0) {
      dbg('[draw-utils] getThemeDrawColors using window.themeColors array, length:', window.themeColors.length, 'first:', window.themeColors[0], 'last:', window.themeColors[window.themeColors.length - 1]);
      return {
         bg: window.themeColors[0] || '#000000',
         fg: window.themeColors[window.themeColors.length - 1] || '#e1e1e1',
         mid: window.themeColors[Math.floor(window.themeColors.length / 2)] || '#787878',
      };
   }
   
   // Fallback to window.currentTheme.data (object form)
   const d = window.currentTheme && window.currentTheme.data;
   dbg('[draw-utils] getThemeDrawColors using window.currentTheme.data, d:', d);
   const result = {
      bg:  (d && d.c0) || '#000000',
      fg:  (d && d.cf) || (d && d.c9) || '#e1e1e1',
      mid: (d && d.c8) || '#787878',
   };
   dbg('[draw-utils] getThemeDrawColors returning:', result);
   return result;
}

/**
 * Get theme colors from ctx or window
 * Handles both function (ctx.themeColors()) and array (window.themeColors) forms
 */
function _getThemeColors(ctx) {
   if (ctx.themeColors && typeof ctx.themeColors === 'function') {
      return ctx.themeColors();
   }
   if (window.themeColors) {
      return window.themeColors;
   }
   // Fallback to window.currentTheme
   const d = window.currentTheme && window.currentTheme.data;
   return {
      bg: (d && d.c0) || '#000000',
      fg: (d && d.cf) || '#e1e1e1',
      mid: (d && d.c8) || '#787878',
   };
}

/**
 * Shared canvas setup — fill bg, set stroke color.
 */
function _prepCanvas(canvas) {
   const { bg, fg } = getThemeDrawColors();
   const c = canvas.getContext('2d');
   c.fillStyle = bg;
   c.fillRect(0, 0, canvas.width, canvas.height);
   c.strokeStyle = fg;
   c.lineWidth = 1;
   return c;
}

/**
 * Draw waveform from Int16Array or Int32Array PCM (min/max column algorithm).
 * Handles mono or interleaved stereo (takes channel 0 only).
 * @param {HTMLCanvasElement} canvas
 * @param {Int16Array|Int32Array} pcm
 * @param {number} [channels=1]
 * @param {number} [bitDepth] - Optional bit depth for Int32Array scaling
 */
function drawWaveformFromInt16(canvas, pcm, channels, bitDepth) {
   if (!canvas || !pcm || !pcm.length) return;
   const c = _prepCanvas(canvas);
   const W = canvas.width;
   const H = canvas.height;
   const amp = H / 2;
   const stride = channels || 1;
   let scale;

   if (pcm instanceof Int32Array) {
      if (bitDepth === 16) {
         scale = 32768;
      } else if (bitDepth === 24) {
         scale = 8388608;
      } else if (bitDepth === 32) {
         scale = 2147483648;
      } else {
         scale = 32768;
      }
   } else {
      scale = 32768;
   }

   const n = Math.floor(pcm.length / stride);
   if (n <= 1) {
      c.beginPath(); c.moveTo(0, amp); c.lineTo(W, amp); c.stroke();
      return;
   }

   // Auto-scale: find the actual peak amplitude and use it if it's much
   // smaller than the full scale. This prevents quiet samples from appearing
   // as blank/black cards. Keep at least 5% of full scale to avoid
   // over-amplifying noise.
   let peakAbs = 0;
   for (let i = 0; i < n; i++) {
      const v = Math.abs(pcm[i * stride]);
      if (v > peakAbs) peakAbs = v;
   }
   if (peakAbs > 0 && peakAbs < scale * 0.25) {
      scale = Math.max(peakAbs * 1.2, scale * 0.05);
   }

   if (n < W) {
      // Short sample (fewer samples than pixels): use linear interpolation
      // to draw a connected waveform line across the full canvas width.
      // This prevents sparse/invisible waveforms on short samples.
      c.beginPath();
      for (let i = 0; i < W; i++) {
         const pos = (i / (W - 1)) * (n - 1);
         const idx = Math.floor(pos);
         const frac = pos - idx;
         const v0 = pcm[idx * stride];
         const v1 = (idx + 1 < n) ? pcm[(idx + 1) * stride] : v0;
         const v = v0 + (v1 - v0) * frac;
         const y = amp + (v / scale) * amp;
         if (i === 0) c.moveTo(i, y); else c.lineTo(i, y);
      }
      c.stroke();
   } else {
      // Long sample: min/max per column algorithm
      c.beginPath();
      for (let i = 0; i < W; i++) {
         const start = Math.floor((i / W) * n);
         const end   = Math.max(start + 1, Math.floor(((i + 1) / W) * n));
         let min = scale - 1, max = -(scale);
         for (let k = start; k < end && k < n; k++) {
            const val = pcm[k * stride];
            if (val < min) min = val;
            if (val > max) max = val;
         }
         if (min === max) {
            // Single sample or flat region: draw from center to value
            // so the waveform is visible instead of a zero-length line
            const y = amp + (min / scale) * amp;
            c.moveTo(i, amp);
            c.lineTo(i, y);
         } else {
            const yMin = amp + (min / scale) * amp;
            const yMax = amp + (max / scale) * amp;
            c.moveTo(i, yMin);
            c.lineTo(i, yMax);
         }
      }
      c.stroke();
   }
}

// Alias — Int32 uses same code path (scale differs automatically)
const drawWaveformFromInt32 = (canvas, pcm, channels, bitDepth) => drawWaveformFromInt16(canvas, pcm, channels, bitDepth);

/**
 * Draw waveform from a Web Audio AudioBuffer (Float32, normalized -1..1).
 * Uses min/max per pixel column for a proper waveform display.
 * @param {HTMLCanvasElement} canvas
 * @param {AudioBuffer} audioBuffer
 */
function drawWaveformFromAudioBuffer(canvas, audioBuffer) {
   dbg('[draw-utils] drawWaveformFromAudioBuffer called, canvas:', canvas, 'audioBuffer:', audioBuffer);
   if (!canvas || !audioBuffer) return;
   const c = _prepCanvas(canvas);
   const W = canvas.width;
   const H = canvas.height;
   const amp = H / 2;
   const ch0 = audioBuffer.getChannelData(0);
   const n = ch0.length;
   if (n <= 1) {
      c.beginPath(); c.moveTo(0, amp); c.lineTo(W, amp); c.stroke();
      return;
   }

   // Auto-scale: find the actual peak amplitude and use it if it's much
   // smaller than the full scale. This prevents quiet samples from appearing
   // as blank/black cards. Keep at least 5% of full scale to avoid
   // over-amplifying noise.
   let peakAbs = 0;
   for (let i = 0; i < n; i++) {
      const v = Math.abs(ch0[i]);
      if (v > peakAbs) peakAbs = v;
   }
   let scale = 1;
   if (peakAbs > 0 && peakAbs < 0.25) {
      scale = Math.max(peakAbs * 1.2, 0.05);
   }

   if (n < W) {
      // Short sample: linear interpolation to fill canvas width
      c.beginPath();
      for (let i = 0; i < W; i++) {
         const pos = (i / (W - 1)) * (n - 1);
         const idx = Math.floor(pos);
         const frac = pos - idx;
         const v0 = ch0[idx];
         const v1 = (idx + 1 < n) ? ch0[idx + 1] : v0;
         const v = v0 + (v1 - v0) * frac;
         const y = amp + (v / scale) * amp;
         if (i === 0) c.moveTo(i, y); else c.lineTo(i, y);
      }
      c.stroke();
   } else {
      c.beginPath();
      for (let i = 0; i < W; i++) {
         const start = Math.floor((i / W) * n);
         const end = Math.max(start + 1, Math.floor(((i + 1) / W) * n));
         let min = scale, max = -scale;
         for (let k = start; k < end && k < n; k++) {
            const val = ch0[k];
            if (val < min) min = val;
            if (val > max) max = val;
         }
         if (min === max) {
            const y = amp + (min / scale) * amp;
            c.moveTo(i, amp);
            c.lineTo(i, y);
         } else {
            const yMin = amp + (min / scale) * amp;
            const yMax = amp + (max / scale) * amp;
            c.moveTo(i, yMin);
            c.lineTo(i, yMax);
         }
      }
      c.stroke();
   }
   dbg('[draw-utils] drawWaveformFromAudioBuffer completed');
}

/**
 * Draw waveform from a Float32Array (normalized -1..1, e.g. ScripTracker samples).
 * Uses the same min/max per column algorithm as drawWaveformFromInt16 and
 * drawWaveformFromAudioBuffer for consistent waveform rendering everywhere.
 * @param {HTMLCanvasElement} canvas
 * @param {Float32Array} samples
 */
function drawWaveformFromFloat32(canvas, samples) {
   dbg('[draw-utils] drawWaveformFromFloat32 called, canvas:', canvas, 'samples.length:', samples ? samples.length : 'null');
   if (!canvas || !samples || !samples.length) return;
   const c = _prepCanvas(canvas);
   const W = canvas.width;
   const H = canvas.height;
   const amp = H / 2;
   const n = samples.length;
   if (n <= 1) {
      c.beginPath(); c.moveTo(0, amp); c.lineTo(W, amp); c.stroke();
      return;
   }

   // Auto-scale: find the actual peak amplitude and use it if it's much
   // smaller than the full scale. This prevents quiet samples from appearing
   // as blank/black cards. Keep at least 5% of full scale to avoid
   // over-amplifying noise.
   let peakAbs = 0;
   for (let i = 0; i < n; i++) {
      const v = Math.abs(samples[i]);
      if (v > peakAbs) peakAbs = v;
   }
   let scale = 1;
   if (peakAbs > 0 && peakAbs < 0.25) {
      scale = Math.max(peakAbs * 1.2, 0.05);
   }

   if (n < W) {
      // Short sample: linear interpolation to fill canvas width
      c.beginPath();
      for (let i = 0; i < W; i++) {
         const pos = (i / (W - 1)) * (n - 1);
         const idx = Math.floor(pos);
         const frac = pos - idx;
         const v0 = samples[idx];
         const v1 = (idx + 1 < n) ? samples[idx + 1] : v0;
         const v = v0 + (v1 - v0) * frac;
         const y = amp + (v / scale) * amp;
         if (i === 0) c.moveTo(i, y); else c.lineTo(i, y);
      }
      c.stroke();
   } else {
      c.beginPath();
      for (let i = 0; i < W; i++) {
         const start = Math.floor((i / W) * n);
         const end   = Math.max(start + 1, Math.floor(((i + 1) / W) * n));
         let min = scale, max = -scale;
         for (let k = start; k < end && k < n; k++) {
            const val = samples[k];
            if (val < min) min = val;
            if (val > max) max = val;
         }
         if (min === max) {
            const y = amp + (min / scale) * amp;
            c.moveTo(i, amp);
            c.lineTo(i, y);
         } else {
            const yMin = amp + (min / scale) * amp;
            const yMax = amp + (max / scale) * amp;
            c.moveTo(i, yMin);
            c.lineTo(i, yMax);
         }
      }
      c.stroke();
   }
   dbg('[draw-utils] drawWaveformFromFloat32 completed');
}

/**
 * Convert a hex color string (#rrggbb) to an rgba(...) string with the given alpha.
 */
function hexToRgba(hex, alpha) {
   const clean = hex && hex.startsWith('#') ? hex.slice(1) : hex;
   const r = parseInt(clean.slice(0, 2), 16) || 0;
   const g = parseInt(clean.slice(2, 4), 16) || 0;
   const b = parseInt(clean.slice(4, 6), 16) || 0;
   return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Decode an audio ArrayBuffer using the browser's native decodeAudioData,
 * falling back to the AIFF/AIFC parser when the native decoder rejects the
 * file (Chrome/Firefox don't support AIFF — only Safari does).
 *
 * Supports: wav, mp3, mp4, m4a, ogg, flac, aac, opus, aiff, aif, aifc, etc.
 * @param {AudioContext} audioCtx - Web Audio context
 * @param {ArrayBuffer} arrayBuffer - raw audio file data (will be copied)
 * @returns {Promise<AudioBuffer>}
 */
async function decodeAudioDataWithFallback(audioCtx, arrayBuffer) {
   // If no audioCtx provided, use OfflineAudioContext (no user gesture required)
   if (!audioCtx) audioCtx = getDecodeAudioContext();
   try {
      return await audioCtx.decodeAudioData(arrayBuffer.slice(0));
   } catch (e) {
      // Native decoder failed — try AIFF fallback (Chrome/Firefox don't decode AIFF)
      if (isAIFF(arrayBuffer)) {
         const buf = await decodeAIFF(audioCtx, arrayBuffer);
         if (buf) return buf;
      }
      throw e;
   }
}

module.exports = {
   getThemeDrawColors,
   drawWaveformFromInt16,
   drawWaveformFromInt32,
   drawWaveformFromAudioBuffer,
   drawWaveformFromFloat32,
   stopAllAudioPlayback,
   playAudioBuffer,
   getGlobalAudioContext,
   getDecodeAudioContext,
   hexToRgba,
   decodeAudioDataWithFallback,
};

// Also expose to window for legacy compatibility
if (typeof window !== 'undefined') {
   window.stopAllAudioPlayback = stopAllAudioPlayback;
   window.getGlobalAudioContext = getGlobalAudioContext;
}
